import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import {
    getAllCampaigns, 
    getInfo, 
    getTrips, 
    storeCampaignParticipation, 
    storeCampaign, 
    createCampaign,
    deleteCampaign,
    exportCampaign,
    } from '../utils/Fetching';
import { Campaign, CampaignParticipation } from '../data/FrontendTypes';

interface DataContextProps {
    isAdmin: boolean;
    isInAdminMode: boolean;
    toggleAdminMode: () => void;
    campaigns: Campaign[];
    campaignName: string;
    campaignParticipation: CampaignParticipation | null;
    fetchCampaigns: () => void;
    fetchInfo: () => void;
    saveCampaignParticipation: (campaignParticipation: CampaignParticipation) => void;
    updateCampaign: (campaign: Campaign) => void;
    createNewCampaign: () => void;
    deleteCampaignAndFetch: (campaignId: string) => void;
    downloadCampaignData: (campaignId: string) => void;
    dataIsBeingDownloaded: boolean;
}

const DataContext = createContext<DataContextProps | undefined>(undefined);

interface DataProviderProps {
    children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [campaignName, setCampaignName] = useState<string>("");
    const [campaignParticipation, setCampaignParticipation] = useState<CampaignParticipation | null>(null);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [isInAdminMode, setIsInAdminMode] = useState<boolean>(false);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [dataIsBeingDownloaded, setDataIsBeingDownloaded] = useState<boolean>(false); 

    const toggleAdminMode = useCallback(() => {
        setIsInAdminMode(prevMode => !prevMode);
    }, []);

    // Memoize fetch functions to prevent unnecessary re-renders
    const fetchCampaigns = useCallback(async () => {
        const result = await getAllCampaigns();
        setCampaigns(result);
    }, []);

    const fetchInfo = useCallback(async (retryCount = 0) => {
        try {
            const result = await getInfo();
            setCampaignName(result.campaignName);
            setCampaignParticipation(result.campaignParticipation);
            setIsAdmin(result.isAdmin);
            setIsInitialized(true);
        } catch (error) {
            console.error('Failed to fetch initial info:', error);
            // Retry up to 3 times with exponential backoff
            if (retryCount < 3) {
                const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
                console.log(`Retrying fetchInfo in ${delay}ms (attempt ${retryCount + 1}/3)`);
                setTimeout(() => fetchInfo(retryCount + 1), delay);
            } else {
                console.error('Failed to fetch initial info after 3 retries');
                // You might want to show an error message to the user here
            }
        }
    }, []);

    const fetchTrips = useCallback(async () => {
        const result = await getTrips();
        setCampaignParticipation(result);
    }, []);

    const saveCampaignParticipation = useCallback(async (campaignParticipation: CampaignParticipation) => {
        await storeCampaignParticipation(campaignParticipation);
        await fetchTrips();
    }, [fetchTrips]);

    const updateCampaign = useCallback(async (campaign: Campaign) => {
        try {
            await storeCampaign(campaign);
            await fetchCampaigns();
        } catch (error) {
            console.error('Failed to update campaign:', error);
            await fetchCampaigns();
        }
    }, [fetchCampaigns]);

    const createNewCampaign = useCallback(async () => {
        await createCampaign();
        await fetchCampaigns();
    }, [fetchCampaigns]);

    const deleteCampaignAndFetch = useCallback(async (campaignId: string) => {
        await deleteCampaign(campaignId);
        await fetchCampaigns();
    }, [fetchCampaigns]);

    const downloadCampaignData = useCallback(async (campaignId: string) => {
        setDataIsBeingDownloaded(true);
        await exportCampaign(campaignId);
        setDataIsBeingDownloaded(false);
    }, []);

    // Single useEffect for initial data loading
    useEffect(() => {
        fetchInfo();
    }, [fetchInfo]);

    const contextValue: DataContextProps = {
        isAdmin,
        isInAdminMode,
        toggleAdminMode,
        campaigns,
        campaignName,
        campaignParticipation,
        fetchCampaigns,
        fetchInfo,
        saveCampaignParticipation,
        updateCampaign,
        createNewCampaign,
        deleteCampaignAndFetch,
        downloadCampaignData,   
        dataIsBeingDownloaded,
    };

    return (
        <DataContext.Provider value={contextValue}>
            {children}
        </DataContext.Provider>
    );
};

export const useDataContext = (): DataContextProps => {
    const context = useContext(DataContext);
    if (context === undefined) {
        throw new Error('useDataContext must be used within a DataProvider');
    }
    return context;
};